import { FC, SetStateAction, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  applyCustomFormFillTemplate,
  Asset,
  autoFillCustomFormRelatedAndDateFields,
  CUSTOM_FORM_MODE,
  Customer,
  CustomForm,
  CustomFormAction,
  CustomFormFiller,
  CustomFormFillerRef,
  CustomFormGenerateAndSavePdfResult,
  CustomFormGenerateAndSavePdfResultType,
  CustomFormMode,
  CustomFormPageFieldUuidToFileEntitiesMap,
  CustomFormPageFieldUuidToSignatureBase64Map,
  CustomFormPageUuidToOfflineBackgroundFileUrlMap,
  CustomFormPdfPreviewResult,
  CustomFormRelatedObjectType,
  CustomFormReport,
  CustomFormReportType,
  CustomFormSaveResult,
  CustomFormSendResult,
  CustomStatus,
  deepCopy,
  DRAFT_CUSTOM_FORM,
  DUMMY_ID,
  ErrorView,
  FileEntity,
  FileEntityType,
  FileEntityUuidToFileMap,
  FpCompany,
  FpPostMessageEvent,
  Invoice,
  Job,
  MaintenanceAgreement,
  postMessageToParentWindows,
  Project,
  PUBLIC_LINK_OBJECT_TYPE,
  PublicLinkObjectType,
  SendEmailFormData,
  SendSmsFormData,
  ToastService,
  useIsMobileOrTablet,
  useListenForFpPostMessages,
  usePreloaderContext,
  User,
  wait,
  CustomFormSendResultType,
  CustomFormFilledCommunicationTemplate, useReactComponentsLibraryContext,
  LocalizationContextProvider,
} from '@flicent/fieldpulse-react-component-library-frontend';

import {
  IMAGE_FILE_URLS_VERSIONS,
  OPEN_NOT_FROM_EMAIL_SEARCH_PARAM,
  OPEN_NOT_FROM_EMAIL_SEARCH_PARAM_YES_VALUE
} from '@/constants';
import {
  CustomFormActionEvent,
  CustomFormChangeModeChangedEvent,
  CustomFormOfflineSaveEvent,
  CustomFormOpenEntityEvent,
  CustomFormPrintPdfEvent,
  CustomFormSetModifiedEvent,
  CustomFormUpdateEvent, CustomFormUploadSelectedFilesEvent,
  CustomFormUploadSelectedFilesEventDataFileUuidToLatestFileDetails, ParentCustomFormSelectedFileUploadResponse
} from '@/interfaces';
import {
  createCustomFormFieldsFilesRequest,
  createCustomFormRequest,
  deleteCustomFormFieldsFilesRequest,
  generateAndSaveCustomFormReportPdfRequest,
  generateCustomFormReportPdfPreviewRequest,
  generateCustomFormReportPdfPreviewWithImagesRequest,
  getCustomFormAdditionalDataRequest,
  getCustomFormFilesRequest,
  getCustomFormFilledCommunicationTemplateRequest,
  getCustomFormRequest,
  initCustomFormCreationRequest,
  getFillTemplateCustomFormRequest,
  getFillTemplateCustomFormsRequest,
  initCustomFormReportPdfPreviewFilesSummarySectionRequest,
  InitCustomFormReportPreviewFilesSummarySectionResponseDataType,
  modifyCustomFormFieldsFilesRequest,
  sendCustomFormReportPublicLinkRequest,
  updateCustomFormRequest,
  updateCustomFormStatusRequest,
} from '@/services';
import { CustomFormPageUriParams, CustomFormParentPostMessageEvent } from '@/types';
import {
  constructCustomFormPageUrl,
  CreateFileWithParentProgressFunctionParam, uploadCustomFormFieldsFilesUpdates,
  UploadCustomFormFieldsFilesUpdatesReturnType
} from '@/utils';
import { useCustomFormAnalyticsInternalContext } from '@/contexts';
import { useCustomFormWebsocketMessages } from '@/hooks';
import { useCustomFormTranslation } from '../i18n';


const STANDALONE_OBJECT_TYPE: string = 'standalone';

const CUSTOM_FORM_MODES: CustomFormMode[] = Object.values(CUSTOM_FORM_MODE);

const isCustomFormParentPostMessageEvent = (data: FpPostMessageEvent): data is CustomFormParentPostMessageEvent => {
  return data.type === 'fp-form';
};

const getIsDraft = (id: string | undefined): boolean => id === undefined;

interface CustomFormPageComponentProps {
  onChangePageTitle: (title: string) => void;
}

export const CustomFormPageComponent: FC<CustomFormPageComponentProps> = ({ onChangePageTitle }) => {
  const { id } = useParams<CustomFormPageUriParams>();
  const [searchParams] = useSearchParams();
  const token: string | null = searchParams.get('token');
  const objectIdSearchParam: string | null = searchParams.get('object_id');

  let objectType: PublicLinkObjectType = searchParams.get('object_type') as PublicLinkObjectType;
  if (!objectType || objectType === STANDALONE_OBJECT_TYPE) {
    objectType = PUBLIC_LINK_OBJECT_TYPE.custom_form;
  }

  const objectId: number = parseInt(objectIdSearchParam || '') || DUMMY_ID;

  const templateId: string | null = searchParams.get('template_id');
  const modeSearchParam: string | null = searchParams.get('mode');

  let initialMode: CustomFormMode | undefined;
  if (modeSearchParam && CUSTOM_FORM_MODES.includes(modeSearchParam as CustomFormMode)) {
    initialMode = modeSearchParam as CustomFormMode;
  }

  const initPreviewFilesSectionRef = useRef<Promise<InitCustomFormReportPreviewFilesSummarySectionResponseDataType | null> | null>(null);
  const formFillerRef = useRef<CustomFormFillerRef | null>(null);

  const { t } = useCustomFormTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobileOrTablet: boolean = useIsMobileOrTablet();
  const preloaderContext = usePreloaderContext();
  const { trackFormAttached, trackFormSubmitted, trackFormSigned } = useCustomFormAnalyticsInternalContext();
  const { isOffline, isInsideMobileApp } = useReactComponentsLibraryContext();
  const uploadSelectedFilesWithParentPromiseResolveRef = useRef<((responses: Record<string, ParentCustomFormSelectedFileUploadResponse>) => void) | null>(null);
  const uploadSelectedFilesWithParentProgressFunctionsRef = useRef<CreateFileWithParentProgressFunctionParam | null>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [initialPageFieldUuidToFileEntitiesMap, setInitialPageFieldUuidToFileEntitiesMap] = useState<CustomFormPageFieldUuidToFileEntitiesMap>({});
  const [formHasFiles, setFormHasFiles] = useState<boolean>(false);
  const [canManageFormFiles, setCanManageFormFiles] = useState<boolean>(false);
  const [formHasImageFiles, setFormHasImageFiles] = useState<boolean>(false);
  const [formFiles, setFormFiles] = useState<FileEntity[] | null>(null);
  const [isFormFilesLoading, setIsFormFilesLoading] = useState<boolean>(false);
  const [pageFieldUuidToSignatureBase64Map, setPageFieldUuidToSignatureBase64Map] = useState<CustomFormPageFieldUuidToSignatureBase64Map>({});
  const [pageFieldUuidToFileEntitiesMap, setPageFieldUuidToFileEntitiesMap] = useState<CustomFormPageFieldUuidToFileEntitiesMap>({});
  const [failedToCreateFileEntitiesUuids, setFailedToCreateFileEntitiesUuids] = useState<string[]>([]);
  const [fileEntityUuidToFileMap, setFileEntityUuidToFileMap] = useState<FileEntityUuidToFileMap>({});
  const [pageUuidToOfflineBackgroundFileUrlMap, setPageUuidToOfflineBackgroundFileUrlMap] = useState<CustomFormPageUuidToOfflineBackgroundFileUrlMap>({});
  const [user, setUser] = useState<User | null>(null);
  const [company, setCompany] = useState<FpCompany | null>(null);
  const [initialForm, setInitialForm] = useState<CustomForm>(DRAFT_CUSTOM_FORM);
  const [form, setForm] = useState<CustomForm>(DRAFT_CUSTOM_FORM);
  const [error, setError] = useState<string | null>(null);
  const [customStatuses, setCustomStatuses] = useState<CustomStatus[] | null>(null);
  const [isRootRelatedEntityActsAsTemplate, setIsRootRelatedEntityActsAsTemplate] = useState<boolean>(false);
  const [relatedCustomer, setRelatedCustomer] = useState<Customer | null>(null);
  const [relatedJob, setRelatedJob] = useState<Job | null>(null);
  const [relatedEstimates, setRelatedEstimates] = useState<Invoice[] | null>(null);
  const [relatedInvoices, setRelatedInvoices] = useState<Invoice[] | null>(null);
  const [relatedProject, setRelatedProject] = useState<Project | null>(null);
  const [relatedMaintenanceAgreements, setRelatedMaintenanceAgreements] = useState<MaintenanceAgreement[] | null>(null);
  const [relatedAssets, setRelatedAssets] = useState<Asset[] | null>(null);
  const [isRelatedEntitiesLoading, setIsRelatedEntitiesLoading] = useState<boolean>(true);
  const [isDraft, setIsDraft] = useState<boolean>(getIsDraft(id));
  const [isFormAlreadyCreatedInDatabase, setIsFormAlreadyCreatedInDatabase] = useState(!isDraft);

  const pageTitle = loading ? t('common.loading') : t('common.pageTitle', { name: form.name || t('common.new') });

  // Update Page Title
  useEffect(() => {
    onChangePageTitle(pageTitle);
  }, [pageTitle, onChangePageTitle]);

  // Keep `formHasFiles` and `formHasImageFiles` actual
  useEffect(() => {
    let newFormHasFiles = formHasFiles;
    let newFormHasImageFiles = formHasImageFiles;

    for (const fileEntities of Object.values(pageFieldUuidToFileEntitiesMap)) {
      for (const fileEntity of fileEntities) {
        newFormHasFiles = true;
        if (new FileEntityType(fileEntity).isImage()) {
          newFormHasImageFiles = true;
          break;
        }
      }
    }

    if (formFiles) {
      for (const fileEntity of formFiles) {
        newFormHasFiles = true;
        if (new FileEntityType(fileEntity).isImage()) {
          newFormHasImageFiles = true;
        }
      }
    }

    setFormHasFiles(newFormHasFiles);
    setFormHasImageFiles(newFormHasImageFiles);
  }, [formFiles, pageFieldUuidToFileEntitiesMap]);

  const setInitialAndCurrentForms = (form: SetStateAction<CustomForm>): void => {
    setForm((prevForm: CustomForm) => {
      const newForm = typeof form === 'function' ? form(prevForm) : form;

      setInitialForm(deepCopy(newForm));

      // This event is used by the mobile application to keep the form data in sync
      const setFormPostMessage: CustomFormUpdateEvent = {
        type: 'fp-form',
        action: 'set-form',
        data: newForm,
      };
      postMessageToParentWindows(setFormPostMessage);
      // ==================================================================

      return newForm;
    });
  };

  const setInitialAndCurrentFormsTemporaryId = (formTemporaryId: number): void => {
    setInitialAndCurrentForms((prevForm) => ({
      ...prevForm,
      id: formTemporaryId,
    }));
  };

  const setInitialAndCurrenPageFieldUuidToFileEntitiesMap = (pageFieldUuidToFileEntitiesMap: SetStateAction<CustomFormPageFieldUuidToFileEntitiesMap>): void => {
    setInitialPageFieldUuidToFileEntitiesMap(typeof pageFieldUuidToFileEntitiesMap === 'function' ? pageFieldUuidToFileEntitiesMap : deepCopy(pageFieldUuidToFileEntitiesMap));
    setPageFieldUuidToFileEntitiesMap(pageFieldUuidToFileEntitiesMap);
  };

  useCustomFormWebsocketMessages({
    token: isOffline ? null : token,
    onPdfGenerated: (formId: number) => {
      if (form.id === formId) {
        formFillerRef.current?.handlePdfGeneratedAndSavedInQueue();
      }
    },
    onPdfGenerationError: (formId: number, errorDetail: string) => {
      if (form.id === formId) {
        formFillerRef.current?.handlePdfGenerateAndSaveErrorInQueue(errorDetail);
      }
    },
    onReportSent: (formId: number, emailSuccess: boolean, smsSuccess: boolean) => {
      if (form.id === formId) {
        formFillerRef.current?.handleReportSentInQueue(emailSuccess, smsSuccess);
      }
    },
    onReportSendError: (formId: number, errorDetail: string) => {
      if (form.id === formId) {
        formFillerRef.current?.handleReportSendErrorInQueue(errorDetail);
      }
    },
  });

  // Set `isDraft` and `isCreatedInDatabase` based on the `id` route parameter
  useEffect(() => {
    const isDraftNew: boolean = getIsDraft(id);
    setIsDraft(isDraftNew);
    setIsFormAlreadyCreatedInDatabase(!isDraftNew);
  }, [id]);

  const handleStartLoadingPrimaryData = () => {
    setLoading(true);
    preloaderContext.showFullPagePreloader();
  };

  const handleEndLoadingPrimaryData = () => {
    preloaderContext.hideFullPagePreloader();
    setLoading(false);
  };

  // Fetch Template
  useEffect(() => {
    if (isOffline) {
      return;
    }

    if (!token) {
      // We don't set error here, because the token absence is handled in the JSX
      return;
    }

    const loadAdditionalData = async (user: User, statusWorkflowId: number | null, objectType: PublicLinkObjectType, objectId: number) => {
      if (!statusWorkflowId && (!objectId || !objectType)) {
        setIsRelatedEntitiesLoading(false);
        return;
      }

      setIsRelatedEntitiesLoading(true);

      getCustomFormAdditionalDataRequest({ token, statusWorkflowId, objectType, objectId })
        .then(({
                 custom_statuses,
                 is_root_related_entity_acts_as_template,
                 related_customer,
                 related_estimates,
                 related_invoices,
                 related_job,
                 related_project,
                 related_maintenance_agreements,
                 related_assets,
               }) => {
          setCustomStatuses(custom_statuses);
          setIsRootRelatedEntityActsAsTemplate(is_root_related_entity_acts_as_template);
          setRelatedCustomer(related_customer);
          setRelatedEstimates(related_estimates);
          setRelatedInvoices(related_invoices);
          setRelatedJob(related_job);
          setRelatedProject(related_project);
          setRelatedMaintenanceAgreements(related_maintenance_agreements);
          setRelatedAssets(related_assets);

          if (!isFormAlreadyCreatedInDatabase) {
            const newProjectId = related_project?.id || null;
            const newJobId = related_job?.id || null;
            // We prioritize Invoice over Estimate, because Invoice is a more advanced stage of the sales process.
            // We set only one of these IDs, because the form can be linked to only one entity.
            const newInvoiceId = related_invoices?.[0]?.id || related_estimates?.[0]?.id || null;

            setInitialForm((prevForm => ({
              ...prevForm,
              project_id: newProjectId,
              job_id: newJobId,
              invoice_id: newInvoiceId,
            })));

            setForm((prevForm) => ({
              ...prevForm,
              // Autofill ****Related**** Fields
              pages: autoFillCustomFormRelatedAndDateFields({
                user,
                relatedEntities: {
                  isRootRelatedEntityActsAsTemplate: is_root_related_entity_acts_as_template,
                  relatedAssets: related_assets,
                  relatedCustomer: related_customer,
                  relatedEstimates: related_estimates,
                  relatedInvoices: related_invoices,
                  relatedJob: related_job,
                  relatedMaintenanceAgreements: related_maintenance_agreements,
                  relatedProject: related_project,
                },
                pages: prevForm.pages,
              }),
              project_id: newProjectId,
              job_id: newJobId,
              invoice_id: newInvoiceId,
            }));
          }
        })
        .catch((error: Error) => {
          ToastService.middleScreenErrorToast(error.message);
        })
        .finally(() => setIsRelatedEntitiesLoading(false));
    };

    if (!isFormAlreadyCreatedInDatabase) {
      if (!templateId) {
        setError(t('customFormPage.errors.brokenLinkNoTemplateId'));
        return;
      }

      handleStartLoadingPrimaryData();

      initCustomFormCreationRequest({ templateId: parseInt(templateId!), token, objectType, objectId })
        .then(({ can_manage_form_files, user, company, form }) => {
          // Autofill ****Date**** Fields
          form.pages = autoFillCustomFormRelatedAndDateFields({
            user,
            relatedEntities: {
              isRootRelatedEntityActsAsTemplate: false,
              relatedAssets: [],
              relatedCustomer: null,
              relatedEstimates: [],
              relatedInvoices: [],
              relatedJob: null,
              relatedMaintenanceAgreements: [],
              relatedProject: null,
            },
            pages: form.pages,
          });

          setCanManageFormFiles(can_manage_form_files);
          setUser(user);
          setCompany(company);
          setInitialAndCurrentForms(form);

          loadAdditionalData(user, form.status_workflow_id, objectType, objectId);
        })
        .catch((error: Error) => {
          setError(error.message);
        })
        .finally(handleEndLoadingPrimaryData);
    } else if (id) {
      if (form.id !== parseInt(id)) {
        handleStartLoadingPrimaryData();

        getCustomFormRequest({ id, token })
          .then(({
                   can_manage_form_files,
                   user,
                   company,
                   form_has_files,
                   form_has_image_files,
                   form,
                   page_field_uuid_to_signature_base64_map,
                   page_field_uuid_to_file_entities_map,
                 }) => {
            setCanManageFormFiles(can_manage_form_files);
            setFormHasFiles(form_has_files);
            setFormHasImageFiles(form_has_image_files);
            setUser(user);
            setCompany(company);
            setInitialAndCurrentForms(form);
            setPageFieldUuidToSignatureBase64Map(page_field_uuid_to_signature_base64_map);
            setInitialAndCurrenPageFieldUuidToFileEntitiesMap(page_field_uuid_to_file_entities_map);

            loadAdditionalData(user, form.status_workflow_id, form.object_type, form.object_id);
          })
          .catch((error: Error) => {
            setError(error.message);
          })
          .finally(handleEndLoadingPrimaryData);
      }
    }
  }, [isOffline, id, objectType, objectId]);

  // Update form object_type and object_id when they change in the URL
  useEffect(() => {
    setInitialAndCurrentForms((prevForm: CustomForm) => ({
      ...prevForm,
      object_type: objectType,
      object_id: objectId,
    }));
  }, [objectType, objectId]);

  // Listen for post messages from the parent window
  useListenForFpPostMessages((event: FpPostMessageEvent) => {
    if (isCustomFormParentPostMessageEvent(event)) {
      const TEMPORARY_FALLBACK_CAN_MANAGE_FORM_FILES = true; // FIXME: remove after the MOB-244 is released

      switch (event.action) {
        case 'save':
          formFillerRef.current?.handleClickSaveOrUpdate();
          break;
        case 'init-draft-offline-form':
          // Autofill ****Date**** Fields
          event.data.template.pages = autoFillCustomFormRelatedAndDateFields({
            user: event.data.user,
            relatedEntities: {
              isRootRelatedEntityActsAsTemplate: false,
              relatedAssets: [],
              relatedCustomer: null,
              relatedEstimates: [],
              relatedInvoices: [],
              relatedJob: null,
              relatedMaintenanceAgreements: [],
              relatedProject: null,
            },
            pages: event.data.template.pages,
          });

          setIsDraft(true);
          setCanManageFormFiles(event.data.canManageFormFiles ?? TEMPORARY_FALLBACK_CAN_MANAGE_FORM_FILES);
          setUser(event.data.user);
          setCompany(event.data.company);
          setInitialAndCurrentFormsTemporaryId(event.data.formTemporaryId);
          setInitialAndCurrentForms((prevForm) => ({
            ...prevForm,
            name: event.data.template.name,
            custom_form_template_id: event.data.template.id!,
            template_name: event.data.template.name,
            is_able_to_save_form_as_template: event.data.template.is_able_to_save_form_as_template,
            is_able_to_create_bypassing_required_fields: event.data.template.is_able_to_create_bypassing_required_fields,
            is_able_to_edit: event.data.template.is_able_to_edit,
            is_status_enabled: event.data.template.is_status_enabled,
            status_workflow_id: event.data.template.status_workflow_id,
            pages: event.data.template.pages,
            formulas: event.data.template.formulas,
          }));
          setCustomStatuses(event.data.customStatuses);
          setPageUuidToOfflineBackgroundFileUrlMap(event.data.pageUuidToOfflineBackgroundFileUrlMap);
          handleEndLoadingPrimaryData();
          setIsRelatedEntitiesLoading(false);
          break;
        case 'init-existing-offline-form':
          setIsDraft(event.data.isDraft);
          setCanManageFormFiles(event.data.canManageFormFiles ?? TEMPORARY_FALLBACK_CAN_MANAGE_FORM_FILES);
          setUser(event.data.user);
          setCompany(event.data.company);
          setInitialAndCurrentForms(event.data.form);
          setInitialAndCurrenPageFieldUuidToFileEntitiesMap(event.data.pageFieldUuidToFileEntitiesMap);
          setPageFieldUuidToSignatureBase64Map(event.data.pageFieldUuidToSignatureBase64Map);
          setPageUuidToOfflineBackgroundFileUrlMap(event.data.pageUuidToOfflineBackgroundFileUrlMap);
          setCustomStatuses(event.data.customStatuses);

          handleEndLoadingPrimaryData();
          setIsRelatedEntitiesLoading(false);
          break;
        case 'selected-files-uploaded':
          if (uploadSelectedFilesWithParentPromiseResolveRef.current) {
            uploadSelectedFilesWithParentPromiseResolveRef.current(event.data.upload_responses);
          }
          break;
        case 'selected-file-upload-progress':
          if (uploadSelectedFilesWithParentProgressFunctionsRef.current) {
            uploadSelectedFilesWithParentProgressFunctionsRef.current(event.data.file_uuid, event.data.progress);
          }
          break;
      }
    }
  });

  const navigateToCustomFormById = (id: number) => {
    const route = constructCustomFormPageUrl({ id: id.toString() })
      + '' + location.search
      + '' + location.hash;

    navigate(route, {
      replace: true,
    });
  };

  const uploadFormFieldsFiles = async (newForm: CustomForm): Promise<UploadCustomFormFieldsFilesUpdatesReturnType> => {
    const pageFieldUuidToFieldIdMap: Record<string, number> = {};
    for (const page of newForm.pages) {
      for (const page_field of page.page_fields) {
        pageFieldUuidToFieldIdMap[page_field.uuid] = page_field.field.id;
      }
    }

    const result = await uploadCustomFormFieldsFilesUpdates(
      t,
      isInsideMobileApp,
      isMobileOrTablet,
      initialPageFieldUuidToFileEntitiesMap,
      pageFieldUuidToFileEntitiesMap,
      fileEntityUuidToFileMap,
      (chunks, onProgress) => {
        const fileUuidToLatestFileDetails: CustomFormUploadSelectedFilesEventDataFileUuidToLatestFileDetails = {};
        for (const { pageFieldUuid, fileEntity } of chunks) {
          if (!fileUuidToLatestFileDetails[fileEntity.uuid]) {
            fileUuidToLatestFileDetails[fileEntity.uuid] = [];
          }

          fileUuidToLatestFileDetails[fileEntity.uuid].push({
            field_id: pageFieldUuidToFieldIdMap[pageFieldUuid],
            display_name: fileEntity.display_name,
            notes: fileEntity.notes,
          });
        }

        postUploadSelectedFilesMessage(fileUuidToLatestFileDetails);

        return new Promise((resolve) => {
          uploadSelectedFilesWithParentPromiseResolveRef.current = resolve;
          uploadSelectedFilesWithParentProgressFunctionsRef.current = onProgress;
        });
      },
      ({ pageFieldUuid, file, fileEntity }, onProgress) => createCustomFormFieldsFilesRequest({
        token: token!,
        fileEntityUuid: fileEntity.uuid,
        fieldId: pageFieldUuidToFieldIdMap[pageFieldUuid],
        extension: fileEntity.extension,
        displayName: fileEntity.display_name,
        notes: fileEntity.notes,
        file: file,
      }, onProgress),
      (fileEntities: FileEntity[]) => modifyCustomFormFieldsFilesRequest({
        token: token!,
        formId: newForm.id,
        fileEntities,
      }),
      (fileEntitiesIds: number[]) => deleteCustomFormFieldsFilesRequest({
        token: token!,
        formId: newForm.id,
        fileEntitiesIds,
      }),
      preloaderContext.setFullPagePreloaderProgress,
    );

    setInitialPageFieldUuidToFileEntitiesMap(result.newInitialPageFieldUuidToFileEntitiesMap);
    setPageFieldUuidToFileEntitiesMap(result.newCurrentPageFieldUuidToFileEntitiesMap);
    setFileEntityUuidToFileMap(result.newCurrentFileEntityUuidToFileMap);
    setFailedToCreateFileEntitiesUuids(result.failedToCreateFileEntitiesUuids);

    return result;
  };

  const postSaveInOfflineMessage = (): void => {
    const saveInOfflinePostMessageEvent: CustomFormOfflineSaveEvent = {
      type: 'fp-form',
      action: 'save-in-offline',
      data: {
        form,
        pageFieldUuidToFileEntitiesMap,
      }
    };

    postMessageToParentWindows(saveInOfflinePostMessageEvent);
  };

  const postUploadSelectedFilesMessage = (fileUuidToLatestFileDetails: CustomFormUploadSelectedFilesEventDataFileUuidToLatestFileDetails): void => {
    const uploadSelectedFilesPostMessageEvent: CustomFormUploadSelectedFilesEvent = {
      type: 'fp-form',
      action: 'upload-selected-files',
      data: {
        image_file_urls_versions: IMAGE_FILE_URLS_VERSIONS,
        file_uuid_to_latest_file_details: fileUuidToLatestFileDetails,
      }
    };

    postMessageToParentWindows(uploadSelectedFilesPostMessageEvent);
  };

  const postCreateMessage = (form: CustomForm): void => {
    // This event is used by the mobile application and webapp: to navigate to the view mode
    const createPostMessageEvent: CustomFormUpdateEvent = {
      type: 'fp-form',
      action: 'create',
      data: form
    };

    postMessageToParentWindows(createPostMessageEvent);
  };

  const postEditMessage = (form: CustomForm): void => {
    // This event is used by the mobile application and webapp: to navigate to the view mode
    const updatePostMessageEvent: CustomFormUpdateEvent = {
      type: 'fp-form',
      action: 'edit',
      data: form
    };

    postMessageToParentWindows(updatePostMessageEvent);
  };

  const saveFormInOfflineMode = async (): Promise<CustomFormSaveResult> => {
    if (!isOffline) {
      throw new Error('Cannot save form in offline mode when the application is not in offline mode');
    }

    postSaveInOfflineMessage();

    return {
      form,
      success: true
    };
  };

  const saveFormInOnlineMode = async (): Promise<CustomFormSaveResult> => {
    if (isOffline) {
      throw new Error('Cannot save form in offline mode when the application is not in offline mode');
    }

    const saveResult: CustomFormSaveResult = {
      form,
      success: false
    };

    if (isMobileOrTablet) {
      preloaderContext.showFullPagePreloader();
    }

    try {
      // 1. Upload FORM
      const {
        is_form_attached_to_record,
        new_signer_names,
        form: newForm,
        page_field_uuid_to_signature_base64_map
      } = isFormAlreadyCreatedInDatabase
        ? await updateCustomFormRequest({ token: token!, form, })
        : await createCustomFormRequest({ token: token!, formTemplateId: templateId!, form, });

      setInitialAndCurrentForms(newForm);
      setPageFieldUuidToSignatureBase64Map(page_field_uuid_to_signature_base64_map);
      setIsFormAlreadyCreatedInDatabase(true);

      // Analytics: Form Attached
      if (!isFormAlreadyCreatedInDatabase && is_form_attached_to_record) {
        trackFormAttached({ company: company!, user: user!, form });
      }

      // Analytics: Form Submitted
      trackFormSubmitted({
        isFormAttachedToRecord: is_form_attached_to_record,
        company: company!,
        user: user!,
        form: newForm
      });

      // Analytics: Form Signed
      if (new_signer_names?.length) {
        for (const signerName of new_signer_names) {
          trackFormSigned({ signerName, company: company!, user: user!, form: newForm });
        }
      }

      // 2. Upload FILES
      const { allFailed, somethingFailed } = await uploadFormFieldsFiles(newForm);

      const context: string = isFormAlreadyCreatedInDatabase ? 'updated' : 'created';
      if (allFailed) {
        ToastService.middleScreenWarningToast(t('customFormPage.save.allFilesFailed', { context }));
      } else if (somethingFailed) {
        ToastService.middleScreenWarningToast(t('customFormPage.save.someFilesFailed', { context }));
      } else {
        ToastService.middleScreenToast(t('customFormPage.save.success', { context }));
      }

      // 3. Wait for a little seconds so that the user can read the error message
      await wait(1500);

      saveResult.form = newForm;
      saveResult.success = !somethingFailed;
    } catch (error: any) {
      console.error('Error saving form:', error);
      ToastService.middleScreenErrorToast(error.message);
    }

    if (isMobileOrTablet) {
      preloaderContext.hideFullPagePreloader();
    }

    return saveResult;
  };

  const saveForm = async (): Promise<CustomFormSaveResult> => {
    if (isOffline) {
      return saveFormInOfflineMode();
    } else {
      return saveFormInOnlineMode();
    }
  };

  const getFilledCommunicationTemplateRequest = (): Promise<CustomFormFilledCommunicationTemplate> => {
    return getCustomFormFilledCommunicationTemplateRequest({ token: token!, formId: form.id }).then(({
                                                                                                       response: {
                                                                                                         email_subject,
                                                                                                         email_body,
                                                                                                         sms_body
                                                                                                       }
                                                                                                     }) => ({
      emailSubject: email_subject,
      emailBody: email_body,
      smsBody: sms_body,
    })).catch((error: Error) => {
      ToastService.middleScreenErrorToast(t('customFormPage.errors.communicationTemplateFill', { error: error.message }));

      return {
        emailSubject: form.default_email_subject,
        emailBody: form.default_email_body,
        smsBody: form.default_sms_body,
      };
    });
  };

  const generatePdfPreview = async (formReport: CustomFormReport, isInternalPdf: boolean, isGenerateHtmlPreview: boolean, abortSignal: AbortSignal): Promise<CustomFormPdfPreviewResult> => {
    let filesSummarySectionInitResult: InitCustomFormReportPreviewFilesSummarySectionResponseDataType | null = await initPreviewFilesSectionRef.current;

    if (formReport.is_files_summary_section_visible_on_pdf && !filesSummarySectionInitResult) {
      initPreviewFilesSectionRef.current = initCustomFormReportPdfPreviewFilesSummarySectionRequest({
        token: token!,
        customFormReport: formReport,
      }).catch((error: Error) => {
        ToastService.middleScreenErrorToast(t('customFormPage.errors.filesSummaryInit', { error: error.message }));
        return null;
      });
      filesSummarySectionInitResult = await initPreviewFilesSectionRef.current;
    }

    if (isGenerateHtmlPreview) {
      const { html } = await generateCustomFormReportPdfPreviewWithImagesRequest({
        token: token!,
        isInternalPdf,
        publicLinkIdForFilesSummarySection: filesSummarySectionInitResult?.public_link_id ?? undefined,
        customFormReportIdForFilesSummarySection: filesSummarySectionInitResult?.custom_form_report_id ?? undefined,
        customFormReport: formReport,
      }, abortSignal).catch((error: Error) => {
        if (!abortSignal.aborted) {
          ToastService.middleScreenErrorToast(error.message);
        }
        return { html: null };
      });

      return { pdfFileBlob: null, pdfHtml: html };
    } else {
      const { fileBlob } = await generateCustomFormReportPdfPreviewRequest({
        token: token!,
        isInternalPdf,
        publicLinkIdForFilesSummarySection: filesSummarySectionInitResult?.public_link_id ?? undefined,
        customFormReportIdForFilesSummarySection: filesSummarySectionInitResult?.custom_form_report_id ?? undefined,
        customFormReport: formReport,
      }, abortSignal).catch((error: Error) => {
        if (!abortSignal.aborted) {
          ToastService.middleScreenErrorToast(error.message);
        }
        return { fileBlob: null };
      });

      return { pdfFileBlob: fileBlob, pdfHtml: null };
    }
  };

  const nulishFilesSummarySectionInitResult = () => {
    initPreviewFilesSectionRef.current = null;
  }

  const sendForm = async (emailBody: SendEmailFormData | null, smsBody: SendSmsFormData | null, customFormReport: CustomFormReport, customFormReportType: CustomFormReportType): Promise<CustomFormSendResult> => {
    return await sendCustomFormReportPublicLinkRequest({
      token: token!,
      customFormReportType,
      customFormReport,
      emailBody,
      smsBody,
    }).then(({
               is_task_queued,
               status_id, public_link_url,
               sent_to_be_viewed_at,
               viewed_at,
               sent_to_be_filled_at,
               filled_at,
               email,
               sms,
             }) => {
      if (!is_task_queued) {
        setInitialAndCurrentForms((prevForm) => ({
          ...prevForm,
          status_id,
          sent_to_be_viewed_at,
          viewed_at,
          sent_to_be_filled_at,
          filled_at,
        }));
      }

      return {
        type: is_task_queued ? CustomFormSendResultType.SendQueued : CustomFormSendResultType.SuccessSentImmediately,
        publicLinkUrl: public_link_url + `?${ OPEN_NOT_FROM_EMAIL_SEARCH_PARAM }=${ OPEN_NOT_FROM_EMAIL_SEARCH_PARAM_YES_VALUE }`,
        emailSuccess: Boolean(email),
        smsSuccess: Boolean(sms),
        errorDetail: null,
      };
    }).catch((error: Error) => {
      ToastService.middleScreenErrorToast(error.message);
      return { type: CustomFormSendResultType.Error, publicLinkUrl: '', emailSuccess: false, smsSuccess: false, errorDetail: null, };
    });
  };

  const generateAndSavePdf = async (customFormReport: CustomFormReport, isInternalPdf: boolean): Promise<CustomFormGenerateAndSavePdfResult> => {
    const filesSummarySectionInitResult: InitCustomFormReportPreviewFilesSummarySectionResponseDataType | null = await initPreviewFilesSectionRef.current;

    return await generateAndSaveCustomFormReportPdfRequest({
      token: token!,
      isInternalPdf,
      publicLinkIdForFilesSummarySection: filesSummarySectionInitResult?.public_link_id,
      customFormReportIdForFilesSummarySection: filesSummarySectionInitResult?.custom_form_report_id,
      customFormReport,
    }).then(({ is_task_queued, pdf_file_display_name, pdf_file_url }) => {
      return {
        type: is_task_queued ? CustomFormGenerateAndSavePdfResultType.GenerationQueued : CustomFormGenerateAndSavePdfResultType.SuccessGeneratedImmediately,
        pdfDisplayNameUrl: pdf_file_display_name,
        pdfFileUrl: pdf_file_url,
        errorDetail: null,
      };
    }).catch((error: Error) => {
      ToastService.middleScreenErrorToast(error.message);
      return {
        type: CustomFormGenerateAndSavePdfResultType.Error,
        pdfDisplayNameUrl: null,
        pdfFileUrl: null,
        errorDetail: error.message,
      };
    });
  };

  const sendToBeFilled = async (emailBody: SendEmailFormData | null, smsBody: SendSmsFormData | null, customFormReport: CustomFormReport): Promise<CustomFormSendResult> => {
    return sendForm(emailBody, smsBody, customFormReport, 'fill');
  };

  const sendToBeViewed = async (emailBody: SendEmailFormData | null, smsBody: SendSmsFormData | null, customFormReport: CustomFormReport): Promise<CustomFormSendResult> => {
    return sendForm(emailBody, smsBody, customFormReport, 'view');
  };

  const navigateToResultMode = (wasCreated: boolean, newForm: CustomForm): void => {
    postMessageExitFillMode();

    if (wasCreated) {
      postCreateMessage(newForm);
    } else {
      postEditMessage(newForm);
    }

    navigateToCustomFormById(newForm.id); // Developer code, is used for testing on localhost
  };

  const getFillTemplates = async (): Promise<CustomForm[]> => {
    return await getFillTemplateCustomFormsRequest({ token: token!, templateId: form.custom_form_template_id })
      .then(({ response }) => {
        return response;
      })
      .catch((error: Error) => {
        ToastService.middleScreenErrorToast(error.message);
        throw error;
      });
  };

  const chooseFillTemplate = async (fillTemplateCustomFormId: number) => {
    if (!token) {
      return;
    }

    await getFillTemplateCustomFormRequest({ token, fillTemplateCustomFormId })
      .then(({
               page_field_uuid_to_value_map,
               page_field_uuid_to_settings_map
             }) => {
        setForm((prevForm: CustomForm) => ({
          ...prevForm,
          pages: applyCustomFormFillTemplate(prevForm.pages, page_field_uuid_to_value_map, page_field_uuid_to_settings_map)
        }));

        ToastService.middleScreenToast(t('common.inserted'));
      })
      .catch((error: Error) => {
        ToastService.middleScreenErrorToast(error.message);
        throw error;
      });
  };

  const changeFormStatusId = async (statusId: number | null): Promise<void> => {
    await updateCustomFormStatusRequest({ token: token!, formId: form.id, statusId })
      .then(({ status_id }) => {
        setInitialAndCurrentForms((prevForm) => ({
          ...prevForm,
          status_id,
        }));
      })
      .catch((error: Error) => {
        ToastService.middleScreenErrorToast(error.message);
      });
  };

  const openRelatedEntity = (entityType: CustomFormRelatedObjectType, entityId: number, entityIsTemplate: boolean) => {
    const openEntityEvent: CustomFormOpenEntityEvent = {
      type: 'fp-form',
      action: 'open',
      data: {
        entity: entityType,
        entityId,
        entityIsTemplate,
      }
    };

    postMessageToParentWindows(openEntityEvent);
  };

  const fetchFormFiles = async (): Promise<void> => {
    setIsFormFilesLoading(true);

    await getCustomFormFilesRequest({ token: token!, formId: form.id })
      .then(({ files }) => {
        setFormFiles(files);
      })
      .catch((error: Error) => {
        ToastService.middleScreenErrorToast(error.message);
      });

    setIsFormFilesLoading(false);
  };

  const handleClickAction = (action: CustomFormAction) => {
    const actionEvent: CustomFormActionEvent = {
      type: 'fp-form',
      action,
      data: { formId: form.id },
    };

    postMessageToParentWindows(actionEvent);
  };

  const attachFiles = (): void => {
    handleClickAction(CustomFormAction.attachFilePhoto);
  };

  const postMessageEnterPresentMode = (): void => {
    const enterPresentModeEvent: CustomFormChangeModeChangedEvent = {
      type: 'fp-form',
      action: 'enter-present-mode',
      data: null
    };

    postMessageToParentWindows(enterPresentModeEvent);
  };

  const postMessageExitPresentMode = (): void => {
    const exitPresentModeEvent: CustomFormChangeModeChangedEvent = {
      type: 'fp-form',
      action: 'exit-present-mode',
      data: null
    };

    postMessageToParentWindows(exitPresentModeEvent);
  };

  const postMessageEnterFillMode = (): void => {
    const enterEditModeEvent: CustomFormChangeModeChangedEvent = {
      type: 'fp-form',
      action: 'enter-fill-mode',
      data: null
    };

    postMessageToParentWindows(enterEditModeEvent);
  };

  const postMessageExitFillMode = (): void => {
    const exitEditModeEvent: CustomFormChangeModeChangedEvent = {
      type: 'fp-form',
      action: 'exit-fill-mode',
      data: null
    };

    postMessageToParentWindows(exitEditModeEvent);
  };

  const handleChangeModified = (newModified: boolean) => {
    const modifiedPostMessageEvent: CustomFormSetModifiedEvent = {
      type: 'fp-form',
      action: 'set-modified',
      data: newModified,
    };
    postMessageToParentWindows(modifiedPostMessageEvent);
  };

  const handlePrintPdf = (pdfHtml: string) => {
    const printPdfPostMessageEvent: CustomFormPrintPdfEvent = {
      type: 'fp-form',
      action: 'print-pdf',
      data: { html: pdfHtml },
    };
    postMessageToParentWindows(printPdfPostMessageEvent);
  };

  if (!token) {
    return <Navigate to="/not-allowed"/>;
  }

  if (error) {
    return <ErrorView errorDetails={ error }/>;
  }

  return (
    <LocalizationContextProvider maintenanceAgreementName={ company?.maintenance_agreement_name }
                                 maintenanceAgreementNamePlural={ company?.maintenance_agreement_name_plural }>
      <CustomFormFiller
        ref={ formFillerRef }
        isDraft={ isDraft }
        loading={ loading }
        isFormFilesLoading={ isFormFilesLoading }
        onFormFilesShouldBeFetched={ fetchFormFiles }
        canManageFormFiles={ canManageFormFiles }
        formHasImageFiles={ formHasImageFiles }
        formHasFiles={ formHasFiles }
        formFiles={ formFiles }
        onClickAction={ handleClickAction }
        pageFieldUuidToSignatureBase64Map={ pageFieldUuidToSignatureBase64Map }
        setPageFieldUuidToSignatureBase64Map={ setPageFieldUuidToSignatureBase64Map }
        pageFieldUuidToFileEntitiesMap={ pageFieldUuidToFileEntitiesMap }
        setPageFieldUuidToFileEntitiesMap={ setPageFieldUuidToFileEntitiesMap }
        failedToCreateFileEntitiesUuids={ failedToCreateFileEntitiesUuids }
        fileEntityUuidToFileMap={ fileEntityUuidToFileMap }
        setFileEntityUuidToFileMap={ setFileEntityUuidToFileMap }
        pageUuidToOfflineBackgroundFileUrlMap={ pageUuidToOfflineBackgroundFileUrlMap }
        initialMode={ initialMode }
        isRelatedEntitiesLoading={ isRelatedEntitiesLoading }
        customStatuses={ customStatuses }
        isRootRelatedEntityActsAsTemplate={ isRootRelatedEntityActsAsTemplate }
        relatedCustomer={ relatedCustomer }
        relatedJob={ relatedJob }
        relatedEstimates={ relatedEstimates }
        relatedInvoices={ relatedInvoices }
        relatedProject={ relatedProject }
        relatedMaintenanceAgreements={ relatedMaintenanceAgreements }
        relatedAssets={ relatedAssets }
        onClickRelatedEntity={ openRelatedEntity }
        user={ user }
        company={ company }
        initialForm={ initialForm }
        initialPageFieldUuidToFileEntitiesMap={ initialPageFieldUuidToFileEntitiesMap }
        form={ form }
        onChangeForm={ setForm }
        onSave={ saveForm }
        onGetFilledCommunicationTemplate={ getFilledCommunicationTemplateRequest }
        onEnterFillMode={ postMessageEnterFillMode }
        onNavigateToResultMode={ navigateToResultMode }
        onSaveGeneratedPdf={ generateAndSavePdf }
        onSubmitSendToBeFilled={ sendToBeFilled }
        onSubmitSendToBeViewed={ sendToBeViewed }
        onGeneratePdfPreview={ generatePdfPreview }
        getFillTemplates={ getFillTemplates }
        onChooseFillTemplate={ chooseFillTemplate }
        onChangeFormStatusId={ changeFormStatusId }
        onClickUploadAdditionalFiles={ attachFiles }
        onEnterPresentMode={ postMessageEnterPresentMode }
        onExitPresentMode={ postMessageExitPresentMode }
        onChangeModified={ handleChangeModified }
        onCloseGeneratePdfView={ nulishFilesSummarySectionInitResult }
        onCloseSendToCustomerView={ nulishFilesSummarySectionInitResult }
        onPrintPdfPreview={ handlePrintPdf }
      />
    </LocalizationContextProvider>
  );
};
